import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import { Div } from '@gaiads/telia-react-component-library';
import {
  AssetsList,
  AssetsListTranslations,
  CustomPaginator,
  FilterOption,
  SearchAndFilter,
  SearchAndFilterOnChange,
} from '@cdaas/reusable-components-daas';
import { AssetResponseWithAutoOpen } from '@cdaas/reusable-components-daas/dist/src/asset-api-types/api-types';
import { AssetState, PageInfo } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { routes, withCustomerOrgFormPath } from '../App/routes';
import { assetAdminClient } from '../../api-client/asset-admin-client';

const SearchScopeBaseEnum = {
  Name: 'Name',
  PhoneNumber: 'PhoneNumber',
  Email: 'Email',
  Imei: 'Imei',
  LocationReference: 'LocationReference',
  OrderId: 'OrderId',
} as const;

type SearchScope = (typeof SearchScopeBaseEnum)[keyof typeof SearchScopeBaseEnum];

const CustomerAssets = () => {
  const history = useHistory();
  const { t } = useTranslation('customerAssets');
  const { t: tGlobal } = useTranslation('global');

  const pageSize = 10;
  const organizationId = useParams<{ id: string }>().id;
  const [organizationAssets, setOrganizationAssets] = useState<AssetResponseWithAutoOpen[]>();
  const [assetsPageInfo, setAssetPageInfo] = useState<PageInfo>();
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<boolean>(false);

  const filters: FilterOption[] = [
    {
      label: t('searchAssets.filters.active'),
      value: AssetState.Active,
    },
    {
      label: t('searchAssets.filters.inactive'),
      value: AssetState.Inactive,
    },
    {
      label: t('searchAssets.filters.all'),
      value: '',
      checked: true,
    },
  ];

  const getSearchScopeOption = (searchScope: SearchScope) => {
    switch (searchScope) {
      case SearchScopeBaseEnum.Name:
        return {
          value: SearchScopeBaseEnum.Name,
          label: t('searchAssets.scope.name'),
          placeHolder: t('searchAssets.scope.namePlaceholder'),
        };
      case SearchScopeBaseEnum.PhoneNumber:
        return {
          value: SearchScopeBaseEnum.PhoneNumber,
          label: t('searchAssets.scope.phoneNumber'),
          placeHolder: t('searchAssets.scope.phoneNumberPlaceholder'),
        };
      case SearchScopeBaseEnum.Email:
        return {
          value: SearchScopeBaseEnum.Email,
          label: t('searchAssets.scope.email'),
          placeHolder: t('searchAssets.scope.emailPlaceholder'),
        };
      case SearchScopeBaseEnum.Imei:
        return {
          value: SearchScopeBaseEnum.Imei,
          label: t('searchAssets.scope.imei'),
          placeHolder: t('searchAssets.scope.imeiPlaceholder'),
        };
      case SearchScopeBaseEnum.OrderId:
        return {
          value: SearchScopeBaseEnum.OrderId,
          label: t('searchAssets.scope.orderId'),
          placeHolder: t('searchAssets.scope.orderIdPlaceholder'),
        };
      case SearchScopeBaseEnum.LocationReference:
        return {
          value: SearchScopeBaseEnum.LocationReference,
          label: t('searchAssets.scope.locationReference'),
          placeHolder: t('searchAssets.scope.locationReferencePlaceholder'),
        };
    }
  };

  const initialStateSearchOptions: SearchAndFilterOnChange = {
    filters: [filters[2]],
    search: { value: '', scope: getSearchScopeOption(SearchScopeBaseEnum.Name) },
    checkboxStates: [false, false],
  };
  const [searchOptions, setSearchOptions] = useState<SearchAndFilterOnChange>(initialStateSearchOptions);

  const fetchAssetsByOrganization = async (
    organizationId: string,
    pageSize: number,
    page: number,
    searchScope: SearchScope,
    searchText: string,
    filterEndOfLeaseAssets: boolean,
    filterWaitingForReturnAssets: boolean,
    assetState?: AssetState,
    openAutomaticallyWithId?: string,
  ) => {
    setLoading(true);
    try {
      let response;
      if (!searchText) {
        response = await assetAdminClient.adminAssetsList(
          organizationId,
          pageSize,
          page,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          assetState,
          filterWaitingForReturnAssets,
          filterEndOfLeaseAssets,
        );
      } else {
        response = await assetAdminClient.adminAssetsList(
          organizationId,
          pageSize,
          page,
          searchScope === SearchScopeBaseEnum.Name ? searchText : undefined,
          searchScope === SearchScopeBaseEnum.PhoneNumber ? searchText : undefined,
          searchScope === SearchScopeBaseEnum.Email ? searchText : undefined,
          searchScope === SearchScopeBaseEnum.Imei ? searchText : undefined,
          searchScope === SearchScopeBaseEnum.LocationReference ? searchText : undefined,
          searchScope === SearchScopeBaseEnum.OrderId ? searchText : undefined,
          assetState,
          filterWaitingForReturnAssets,
          filterEndOfLeaseAssets,
        );
      }
      const { contents, page_metadata } = response.data;
      const assetsListData = contents.map(asset => ({ asset: asset, autoOpen: asset.id === openAutomaticallyWithId }));
      setOrganizationAssets(assetsListData);
      setAssetPageInfo(page_metadata);
    } catch {
      setApiError(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchAssetsByOrganization(
      organizationId,
      pageSize,
      0,
      searchOptions.search?.scope?.value as SearchScope,
      searchOptions.search?.value ?? '',
      searchOptions.checkboxStates![0],
      searchOptions.checkboxStates![1],
      (searchOptions.filters?.[0].value as AssetState) || undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (values: SearchAndFilterOnChange) => {
    setSearchOptions(values);
    fetchAssetsByOrganization(
      organizationId,
      pageSize,
      0,
      values.search?.scope?.value as SearchScope,
      values.search?.value ?? '',
      values.checkboxStates![0],
      values.checkboxStates![1],
      (values.filters?.[0].value as AssetState) || undefined,
    );
  };

  const handleOpenAssetButton = (asset: AssetResponseWithAutoOpen['asset']) => {
    history.push(`${withCustomerOrgFormPath(routes.devices, organizationId)}/${asset.id}`);
  };

  const assetsListTranslations: AssetsListTranslations = {
    noDataText: t('noDataText'),
    name: t('name'),
    user: t('user'),
    tableOpenRowButton: t('tableOpenRowButton'),
    errorText: t('apiError'),
    companyDevice: tGlobal('companyDevice'),
    inactiveUser: tGlobal('inactiveUser'),
  };

  const currentSearchScope = getSearchScopeOption(searchOptions.search?.scope?.value as SearchScope);

  return (
    <>
      <SearchAndFilter
        searchConfig={{
          useDebounceInTextSearch: 1000,
          searchInputOrSearchInputWithScopeProps: {
            value: searchOptions.search?.value,
            label: t('searchAssets.searchLabel'),
            placeholder: currentSearchScope.placeHolder,
            scopeOptions: Object.values(SearchScopeBaseEnum).map(searchScope => getSearchScopeOption(searchScope)),
          },
        }}
        filterConfig={{
          text: t('searchAssets.filters.filterActivityLabel'),
          filters,
          radioSelect: true,
        }}
        onChangeCallback={handleSearchChange}
        checkboxConfigs={[
          {
            label: t('searchAssets.filters.filterEndOfLeaseLabel'),
            checked: searchOptions.checkboxStates![0],
          },
          {
            label: t('searchAssets.filters.filterWaitingForReturnLabel'),
            checked: searchOptions.checkboxStates![1],
          },
        ]}
      />
      <Div>
        <AssetsList
          loading={loading}
          assets={organizationAssets}
          buttonClickCallback={handleOpenAssetButton}
          translations={assetsListTranslations}
          error={apiError}
        />
        {!!assetsPageInfo?.total_items && (
          <CustomPaginator
            activatePageInfoObject={assetsPageInfo}
            onPageChange={pageChangeInfo =>
              fetchAssetsByOrganization(
                organizationId,
                pageSize,
                pageChangeInfo.nextChosenPageNumber,
                searchOptions.search?.scope?.value as SearchScope,
                searchOptions.search?.value ?? '',
                searchOptions.checkboxStates![0],
                searchOptions.checkboxStates![1],
                searchOptions.filters?.[0].value as AssetState,
              )
            }
            paginatorStyleProps={{
              pageSelectorProps: { centered: false },
            }}
          />
        )}
      </Div>
    </>
  );
};

export default CustomerAssets;
